import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import { get, post } from '../../services/apiService';
import { isAdmin, isQAManager } from '../../services/authService';

const shortSha = (sha) => (sha ? sha.substring(0, 7) : '—');

const RP_BASE_URL = (process.env.REACT_APP_REPORT_PORTAL_BASE_URL || 'https://lab1reportportal.highq.com').replace(/\/$/, '');
const RP_PROJECT = process.env.REACT_APP_REPORT_PORTAL_REGRESSION_PROJECT || 'develop';

/**
 * Map an ADO work item state to a MUI chip colour token.
 * Resolved → warning (amber), Closed/Done → success (green), anything else → error (red)
 */
const bugChipColor = (state) => {
  if (!state) return 'error';
  const s = state.toLowerCase();
  if (s === 'resolved') return 'warning';
  if (s === 'closed' || s === 'done') return 'success';
  return 'error';
};

const DailyRegressionTab = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);

  // Detail dialogs
  const [testsDialog, setTestsDialog] = useState({ open: false, allTests: [], optimisedIds: new Set(), optimisedPaths: new Set(), duplicatePaths: new Set(), title: '' });
  const [prsDialog, setPrsDialog] = useState({ open: false, prs: [], title: '' });

  // Inline cell editing (rpLaunchId, passPercentage, bugs) — { id, field, value }
  const [editingCell, setEditingCell] = useState(null);

  // RP launch sync loading state — keyed by record _id
  const [syncingLaunch, setSyncingLaunch] = useState({});

  // Changes dialog
  const [changesDialog, setChangesDialog] = useState({ open: false, data: null, title: '' });
  const [changesLoading, setChangesLoading] = useState({});
  const [changesTab, setChangesTab] = useState(0);  // 0=File Changes, 1=Method Changes
  const [fileChangesTab, setFileChangesTab] = useState(0); // 0=Both, 1=Only Drill4J, 2=Only GitHub

  const fetchRecords = async (pageNum, pageSize) => {
    try {
      setLoading(true);
      setError(null);
      const response = await get('/api/recommended-tests/daily-regression', {
        page: pageNum + 1,
        pageSize
      });
      if (response.success) {
        setRecords(response.data || []);
        setTotalRecords(response.pagination?.total || 0);
      } else {
        throw new Error(response.message || 'Failed to fetch daily regression records');
      }
    } catch (err) {
      console.error('Error fetching daily regression records:', err);
      setError(err.message || 'Failed to load daily regression records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const openTestsDialog = (record) => {
    const optimisedIds = new Set(
      (record.recommendedTests || []).map(t => t.testDefinitionId).filter(Boolean)
    );
    const optimisedPaths = new Set(
      (record.recommendedTests || []).map(t => t.testPath).filter(Boolean)
    );
    const allTests = record.allTests?.length > 0 ? record.allTests : (record.recommendedTests || []);
    const pathCount = new Map();
    allTests.forEach(t => {
      const key = t.testPath || t.testName;
      if (key) pathCount.set(key, (pathCount.get(key) || 0) + 1);
    });
    const duplicatePaths = new Set([...pathCount.entries()].filter(([, c]) => c > 1).map(([k]) => k));
    const total = record.allTestsCount || allTests.length;
    const optimised = record.testsCount || optimisedIds.size;
    setTestsDialog({
      open: true,
      allTests,
      optimisedIds,
      optimisedPaths,
      duplicatePaths,
      title: `Tests — ${shortSha(record.currentCommit)} · ${optimised} recommended of ${total} total`
    });
  };

  const openMethodMapsPage = (record) => {
    window.open(`/daily-regression/method-maps?executionId=${encodeURIComponent(record.executionId)}`, '_blank', 'noopener,noreferrer');
  };

  const openPrsDialog = (record) => {
    setPrsDialog({
      open: true,
      prs: record.mergedPRs || [],
      title: `Merged PRs — ${shortSha(record.baselineCommit)}…${shortSha(record.currentCommit)}`
    });
  };

  const handleStatusChange = async (record, newStatus) => {
    // Optimistically update local state
    setRecords(prev => prev.map(r =>
      r._id === record._id ? { ...r, executionStatus: newStatus } : r
    ));
    try {
      await post(`/api/recommended-tests/daily-regression/${record._id}/status`, { executionStatus: newStatus });
    } catch (err) {
      console.error('Error updating status:', err);
      // Revert on failure
      setRecords(prev => prev.map(r =>
        r._id === record._id ? { ...r, executionStatus: record.executionStatus } : r
      ));
    }
  };

  const canEdit = isAdmin() || isQAManager();

  const startCellEdit = (record, field) => {
    if (!canEdit) return;
    const raw = record[field];
    let value;
    if (field === 'bugs') {
      value = Array.isArray(raw)
        ? raw.map(b => (typeof b === 'string' ? b : b?.ticketId)).filter(Boolean).join(', ')
        : '';
    } else {
      value = raw ?? '';
    }
    setEditingCell({ id: record._id, field, value });
  };

  const handleFieldSave = async () => {
    if (!editingCell) return;
    const { id, field, value } = editingCell;
    setEditingCell(null);
    const parsed = field === 'rpLaunchId' ? (value === '' ? null : String(value))
      : field === 'bugs' ? (value === '' ? [] : value.split(',').map(s => s.trim()).filter(Boolean).map(ticketId => ({ ticketId, url: null })))
      : (value === '' ? null : Number(value));
    setRecords(prev => prev.map(r => r._id === id ? { ...r, [field]: parsed } : r));
    try {
      await post(`/api/recommended-tests/daily-regression/${id}/update`, { [field]: parsed });
    } catch (err) {
      console.error('Error saving field:', err);
    }
  };

  const handleSyncLaunch = async (record) => {
    if (!record.rpLaunchId || syncingLaunch[record._id]) return;
    setSyncingLaunch(prev => ({ ...prev, [record._id]: true }));
    try {
      const resp = await post(`/api/recommended-tests/daily-regression/${record._id}/sync-launch`, {});
      if (resp.success) {
        setRecords(prev => prev.map(r =>
          r._id === record._id
            ? {
                ...r,
                passPercentage: resp.data.passPercentage,
                launchStats: {
                  passed: resp.data.passed,
                  total: resp.data.total,
                  failed: resp.data.failed,
                  skipped: resp.data.skipped
                },
                bugs: resp.data.bugs || []
              }
            : r
        ));
      }
    } catch (err) {
      console.error('Error syncing launch stats:', err);
    } finally {
      setSyncingLaunch(prev => ({ ...prev, [record._id]: false }));
    }
  };

  const openChangesDialog = (data, record) => {
    const mergedPRs = record.mergedPRs || [];
    setChangesTab(0);
    setFileChangesTab(0);
    setChangesDialog({
      open: true,
      data: { ...data, mergedPRs },
      title: `Changes — ${shortSha(record.baselineCommit)}…${shortSha(record.currentCommit)} · ${data.methodChangesCount} methods`
    });
  };

  const handleGetChanges = async (record, force = false) => {
    if (!record.repo) return;

    // If cached data is available and this is not a force-refresh, open immediately
    if (!force && record.changesData) {
      openChangesDialog(record.changesData, record);
      return;
    }

    setChangesLoading(prev => ({ ...prev, [record._id]: true }));
    try {
      const params = {
        repo: record.repo,
        baselineCommit: record.baselineCommit,
        currentCommit: record.currentCommit,
        group: record.group,
        application: record.application,
        branch: record.branch
      };
      if (force) params.force = 'true';

      const response = await get('/api/recommended-tests/daily-regression/changes', params);
      if (response.success) {
        // Update the record in local state so subsequent opens skip the API
        setRecords(prev => prev.map(r =>
          r._id === record._id ? {
            ...r,
            changesData: response.data,
            changesFetched: true,
            mergedPRs: response.mergedPRs || [],
            mergedPRsCount: response.mergedPRsCount ?? 0
          } : r
        ));
        openChangesDialog(response.data, record);
      }
    } catch (err) {
      console.error('Error fetching changes:', err);
    } finally {
      setChangesLoading(prev => ({ ...prev, [record._id]: false }));
    }
  };

  if (loading && records.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading daily regression runs…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Daily Regression</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Recommended tests and merged PRs from daily regression runs stored from Drill4J.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={1}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Repository</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Commits</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Tests</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Method Maps</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Changes</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">RP Launch</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Pass %</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Bugs</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Execution Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No daily regression records found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => (
                  <TableRow key={record._id} hover>
                    {/* Repository */}
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{record.repo || '—'}</Typography>
                      {record.branch && (
                        <Chip label={record.branch} size="small" variant="outlined" sx={{ mt: 0.5, mb: 0.25, fontSize: '0.7rem' }} />
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {record.dateTime ? new Date(record.dateTime).toLocaleString() : '—'}
                      </Typography>
                    </TableCell>

                    {/* Commits */}
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Chip
                          label={shortSha(record.currentCommit)}
                          size="small"
                          color="success"
                          component={record.currentCommitUrl ? 'a' : 'span'}
                          href={record.currentCommitUrl || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          clickable={!!record.currentCommitUrl}
                          sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600, width: 'fit-content' }}
                        />
                        <Tooltip
                          title={record.baselineCommitResolution?.wasResolved
                            ? `Requested: ${shortSha(record.baselineCommitResolution.requested)} — not found as a known commit, auto-resolved to this baseline`
                            : ''}
                          disableHoverListener={!record.baselineCommitResolution?.wasResolved}
                          arrow
                        >
                          <Chip
                            label={`${record.baselineCommitResolution?.wasResolved ? '~ ' : ''}${shortSha(record.baselineCommit)}`}
                            size="small"
                            color={record.baselineCommitResolution?.wasResolved ? 'warning' : 'primary'}
                            component={record.baselineCommitUrl ? 'a' : 'span'}
                            href={record.baselineCommitUrl || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            clickable={!!record.baselineCommitUrl}
                            sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600, width: 'fit-content' }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>

                    {/* Tests */}
                    <TableCell align="center">
                      {(record.testsCount > 0 || record.allTestsCount > 0) ? (
                        <Link component="button" variant="body2" onClick={() => openTestsDialog(record)} sx={{ cursor: 'pointer', fontWeight: 600 }}>
                          {record.testsCount}
                          {record.allTestsCount > 0 && record.allTestsCount !== record.testsCount && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
                              ({record.allTestsCount})
                            </Typography>
                          )}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">0</Typography>
                      )}
                    </TableCell>

                    {/* Method Maps */}
                    <TableCell align="center">
                      {record.impactedMethods?.length > 0 ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => openMethodMapsPage(record)}
                          sx={{ cursor: 'pointer' }}
                          title="Open method maps in new tab"
                        >
                          {record.impactedMethods.length}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>

                    {/* Changes */}
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                        {changesLoading[record._id] ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Tooltip title={record.changesFetched ? 'View cached changes' : 'Fetch changes'}>
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => handleGetChanges(record, false)}
                              disabled={!record.repo}
                              sx={{
                                cursor: record.repo ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: record.changesFetched ? 'primary.main' : 'text.secondary',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: record.repo ? 'underline' : 'none' }
                              }}
                            >
                              <CompareArrowsIcon sx={{ fontSize: 14 }} />
                              {record.changesFetched
                                ? `${record.changesData?.methodChangesCount ?? 0} (${record.mergedPRsCount ?? 0})`
                                : 'Changes'}
                            </Link>
                          </Tooltip>
                        )}
                        {record.changesFetched && !changesLoading[record._id] && (
                          <Tooltip title="Refresh changes from API">
                            <IconButton size="small" onClick={() => handleGetChanges(record, true)} disabled={!record.repo} sx={{ p: 0.25 }}>
                              <RefreshIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    {/* RP Launch */}
                    <TableCell align="center">
                      {canEdit && editingCell?.id === record._id && editingCell?.field === 'rpLaunchId' ? (
                        <TextField
                          value={editingCell.value}
                          onChange={(e) => setEditingCell(prev => ({ ...prev, value: e.target.value }))}
                          onBlur={handleFieldSave}
                          onKeyDown={(e) => e.key === 'Enter' && handleFieldSave()}
                          size="small"
                          autoFocus
                          sx={{ width: 90, '& input': { fontSize: '0.78rem', py: 0.4, px: 0.75 } }}
                        />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title={canEdit ? 'Click to edit' : ''}>
                            {record.rpLaunchId ? (
                              <Link
                                href={`${RP_BASE_URL}/ui/#${RP_PROJECT}/launches/all/${record.rpLaunchId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => { if (canEdit) e.stopPropagation(); }}
                                onDoubleClick={() => canEdit && startCellEdit(record, 'rpLaunchId')}
                                sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}
                              >
                                {record.rpLaunchId}
                              </Link>
                            ) : (
                              <Typography
                                variant="body2"
                                onClick={() => startCellEdit(record, 'rpLaunchId')}
                                sx={{ cursor: canEdit ? 'pointer' : 'default', fontFamily: 'monospace', fontSize: '0.82rem', color: '#aaa' }}
                              >
                                —
                              </Typography>
                            )}
                          </Tooltip>
                          {record.rpLaunchId && (
                            <Tooltip title="Sync pass % and bugs from Report Portal">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleSyncLaunch(record)}
                                  disabled={!!syncingLaunch[record._id]}
                                  sx={{ p: 0.25 }}
                                >
                                  <SyncIcon
                                    sx={{
                                      fontSize: 14,
                                      animation: syncingLaunch[record._id] ? 'spin 1s linear infinite' : 'none',
                                      '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } }
                                    }}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>

                    {/* Pass % */}
                    <TableCell align="center">
                      {canEdit && editingCell?.id === record._id && editingCell?.field === 'passPercentage' ? (
                        <TextField
                          value={editingCell.value}
                          onChange={(e) => setEditingCell(prev => ({ ...prev, value: e.target.value }))}
                          onBlur={handleFieldSave}
                          onKeyDown={(e) => e.key === 'Enter' && handleFieldSave()}
                          size="small"
                          autoFocus
                          type="number"
                          inputProps={{ min: 0, max: 100 }}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                          sx={{ width: 80, '& input': { fontSize: '0.78rem', py: 0.4, px: 0.75 } }}
                        />
                      ) : (
                        <Tooltip title={canEdit ? 'Click to edit' : ''}>
                          <Box onClick={() => startCellEdit(record, 'passPercentage')} sx={{ cursor: canEdit ? 'pointer' : 'default', display: 'inline-block', textAlign: 'center' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: record.passPercentage == null ? 'text.disabled'
                                  : record.passPercentage >= 80 ? 'success.main'
                                  : record.passPercentage >= 50 ? 'warning.main'
                                  : 'error.main'
                              }}
                            >
                              {record.passPercentage != null ? `${record.passPercentage}%` : '—'}
                            </Typography>
                            {record.launchStats?.total > 0 && (
                              <Box display="flex" gap={0.5} justifyContent="center" mt={0.5}>
                                <Chip
                                  label={record.launchStats.passed}
                                  size="small"
                                  color="success"
                                  sx={{ fontSize: '0.65rem', height: 18, '& .MuiChip-label': { px: 0.75 } }}
                                />
                                <Chip
                                  label={record.launchStats.failed}
                                  size="small"
                                  color="error"
                                  sx={{ fontSize: '0.65rem', height: 18, '& .MuiChip-label': { px: 0.75 } }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>

                    {/* Bugs */}
                    <TableCell align="center" sx={{ minWidth: 80 }}>
                      {(() => {
                        const bugList = Array.isArray(record.bugs) ? record.bugs : [];
                        // Normalise: each entry may be a string ticketId or { ticketId, url }
                        const bugs = bugList.map(b =>
                          typeof b === 'string' ? { ticketId: b, url: null } : b
                        ).filter(b => b?.ticketId);

                        if (canEdit && editingCell?.id === record._id && editingCell?.field === 'bugs') {
                          return (
                            <TextField
                              value={editingCell.value}
                              onChange={(e) => setEditingCell(prev => ({ ...prev, value: e.target.value }))}
                              onBlur={handleFieldSave}
                              onKeyDown={(e) => e.key === 'Enter' && handleFieldSave()}
                              size="small"
                              autoFocus
                              placeholder="e.g. 914359, 914360"
                              sx={{ width: 140, '& input': { fontSize: '0.78rem', py: 0.4, px: 0.75 } }}
                            />
                          );
                        }

                        if (bugs.length === 0) {
                          return (
                            <Typography
                              variant="body2"
                              onClick={() => canEdit && startCellEdit(record, 'bugs')}
                              sx={{ cursor: canEdit ? 'pointer' : 'default', color: 'text.disabled' }}
                            >
                              —
                            </Typography>
                          );
                        }

                        const visible = bugs.slice(0, 3);
                        const overflow = bugs.length - visible.length;
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, justifyContent: 'center', alignItems: 'center' }}>
                            {visible.map((bug) => {
                              const color = bugChipColor(bug.state);
                              const tooltipText = bug.state || bug.ticketId;
                              const chip = (
                                <Chip
                                  key={bug.ticketId}
                                  label={bug.ticketId}
                                  size="small"
                                  color={color}
                                  variant="filled"
                                  component={bug.url ? 'a' : 'span'}
                                  href={bug.url || undefined}
                                  target={bug.url ? '_blank' : undefined}
                                  rel={bug.url ? 'noopener noreferrer' : undefined}
                                  clickable={!!bug.url}
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              );
                              return (
                                <Tooltip key={bug.ticketId} title={tooltipText}>
                                  <span>{chip}</span>
                                </Tooltip>
                              );
                            })}
                            {overflow > 0 && (
                              <Tooltip title={bugs.slice(3).map(b => `${b.ticketId}${b.state ? ` (${b.state})` : ''}`).join(', ')}>
                                <Chip
                                  label={`+${overflow}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>

                    {/* Execution Status */}
                    <TableCell align="center">
                      <Chip
                        label={record.executionStatus || 'Active'}
                        size="small"
                        color={
                          record.executionStatus === 'Passed' ? 'success' :
                          record.executionStatus === 'Failed' ? 'error' :
                          record.executionStatus === 'Completed' ? 'default' :
                          'primary'
                        }
                        sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                      />
                      {record.executionId && (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ mt: 0.5, fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.65rem', whiteSpace: 'nowrap' }}
                        >
                          {record.executionId}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Tests Detail Dialog */}
      <Dialog open={testsDialog.open} onClose={() => setTestsDialog({ ...testsDialog, open: false })} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{testsDialog.title}</Typography>
            <IconButton size="small" onClick={() => setTestsDialog({ ...testsDialog, open: false })}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
            <Chip label="Selected" size="small" color="success" sx={{ fontSize: '0.68rem', height: 20 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1.5 }}>= recommended</Typography>
            <Chip label="Duplicate" size="small" color="warning" sx={{ fontSize: '0.68rem', height: 20 }} />
            <Typography variant="caption" color="text.secondary">= path appears multiple times</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {testsDialog.allTests.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>No tests available.</Typography>
          ) : (() => {
            const parseTestPath = (testPath) => {
              if (!testPath) return { module: 'Unknown', spec: 'Unknown' };
              const parts = testPath.split(':');
              if (parts.length >= 3) return { module: parts[1], spec: parts.slice(2).join(':') };
              if (parts.length === 2) return { module: parts[0], spec: parts[1] };
              return { module: 'Unknown', spec: testPath };
            };
            const grouped = {};
            testsDialog.allTests.forEach(test => {
              const { module: parsedMod, spec } = parseTestPath(test.testPath);
              const mod = test.metadata?.module || parsedMod;
              if (!grouped[mod]) grouped[mod] = {};
              if (!grouped[mod][spec]) grouped[mod][spec] = [];
              grouped[mod][spec].push(test);
            });
            return (
              <Box>
                {Object.entries(grouped).map(([mod, specs]) => {
                  const specEntries = Object.entries(specs);
                  const totalInMod = specEntries.reduce((s, [, t]) => s + t.length, 0);
                  return (
                    <Box key={mod}>
                      {/* Module row */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 2, py: 0.75,
                        bgcolor: 'grey.100',
                        borderBottom: '1px solid', borderColor: 'divider',
                        borderLeft: '3px solid', borderLeftColor: 'primary.main',
                        position: 'sticky', top: 0, zIndex: 1
                      }}>
                        <Typography variant="body2" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.72rem' }}>
                          {mod}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">({totalInMod})</Typography>
                      </Box>

                      {/* Specs — vertical tree line down the left */}
                      <Box sx={{ borderLeft: '2px solid', borderLeftColor: 'grey.300', ml: '14px' }}>
                        {specEntries.map(([spec, tests]) => (
                          <Box key={spec}>
                            {/* Spec row with horizontal connector */}
                            <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, pr: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                              <Box sx={{ width: 14, height: '1.5px', bgcolor: 'grey.300', flexShrink: 0 }} />
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', ml: 0.75 }}>
                                {spec}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75, flexShrink: 0 }}>({tests.length})</Typography>
                            </Box>

                            {/* Tests — second vertical tree line */}
                            <Box sx={{ borderLeft: '2px solid', borderLeftColor: 'grey.200', ml: '14px' }}>
                              {tests.map((test, j) => {
                                const isOptimised = test.testDefinitionId
                                  ? testsDialog.optimisedIds.has(test.testDefinitionId)
                                  : testsDialog.optimisedPaths.has(test.testPath);
                                const isDuplicate = testsDialog.duplicatePaths.has(test.testPath || test.testName);
                                return (
                                  <Box
                                    key={test.testDefinitionId || j}
                                    sx={{
                                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                      py: 0.6, pr: 2,
                                      bgcolor: isOptimised ? '#e8f5e9' : 'transparent',
                                      borderBottom: '1px solid', borderColor: 'divider'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                      <Box sx={{ width: 10, height: '1.5px', bgcolor: 'grey.200', flexShrink: 0 }} />
                                      <Typography variant="body2" fontWeight={isOptimised ? 600 : 400} sx={{ ml: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {test.testName || '(unnamed)'}
                                      </Typography>
                                    </Box>
                                    {(isOptimised || isDuplicate) && (
                                      <Box display="flex" gap={0.5} sx={{ ml: 2, flexShrink: 0 }}>
                                        {isOptimised && <Chip label="Selected" size="small" color="success" sx={{ fontSize: '0.68rem', height: 20 }} />}
                                        {isDuplicate && <Chip label="Duplicate" size="small" color="warning" sx={{ fontSize: '0.68rem', height: 20 }} />}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestsDialog({ ...testsDialog, open: false })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* PRs Detail Dialog */}
      <Dialog open={prsDialog.open} onClose={() => setPrsDialog({ ...prsDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{prsDialog.title}</Typography>
            <IconButton size="small" onClick={() => setPrsDialog({ ...prsDialog, open: false })}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {prsDialog.prs.length === 0 ? (
            <Typography color="text.secondary">No PR data available for this run.</Typography>
          ) : (
            <List dense>
              {prsDialog.prs.map((pr) => (
                <ListItem key={pr.prNumber} divider>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Link href={pr.url} target="_blank" rel="noopener noreferrer" variant="body2" fontWeight={600}>
                          #{pr.prNumber}
                        </Link>
                        <Typography variant="body2" noWrap>{pr.title}</Typography>
                      </Box>
                    }
                    secondary={
                      <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                        {pr.author && <Chip label={pr.author} size="small" variant="outlined" />}
                        {pr.targetBranch && <Chip label={`→ ${pr.targetBranch}`} size="small" variant="outlined" />}
                        {pr.mergedAt && (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(pr.mergedAt).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrsDialog({ ...prsDialog, open: false })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Changes Dialog */}
      <Dialog
        open={changesDialog.open}
        onClose={() => setChangesDialog({ ...changesDialog, open: false })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{changesDialog.title}</Typography>
            <IconButton size="small" onClick={() => setChangesDialog({ ...changesDialog, open: false })}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {changesDialog.data && (() => {
            const { fileChanges, methodChanges, mergedPRs } = changesDialog.data;
            const { inBoth, onlyInDrill4j, onlyInGithub, summary } = fileChanges;
            const prs = mergedPRs || [];

            return (
              <Box>
                {/* Top-level tabs: File Changes / Method Changes / Merged PRs */}
                <Tabs
                  value={changesTab}
                  onChange={(_, v) => setChangesTab(v)}
                  sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab label={`File Changes (${summary.inBothCount + summary.onlyInDrill4jCount + summary.onlyInGithubCount})`} />
                  <Tab label={`Method Changes (${methodChanges.length})`} />
                  <Tab label={`Merged PRs (${prs.length})`} />
                </Tabs>

                {/* File Changes */}
                {changesTab === 0 && (
                  <Box>
                    <Tabs
                      value={fileChangesTab}
                      onChange={(_, v) => setFileChangesTab(v)}
                      sx={{ px: 2, pt: 1, borderBottom: 1, borderColor: 'divider' }}
                      textColor="secondary"
                      indicatorColor="secondary"
                    >
                      <Tab label={`Both (${summary.inBothCount})`} />
                      <Tab label={`Only Drill4J (${summary.onlyInDrill4jCount})`} />
                      <Tab label={`Only GitHub (${summary.onlyInGithubCount})`} />
                    </Tabs>

                    <Box sx={{ p: 2 }}>
                      {fileChangesTab === 0 && <FileTable files={inBoth} showStatus />}
                      {fileChangesTab === 1 && <FileTable files={onlyInDrill4j} />}
                      {fileChangesTab === 2 && <FileTable files={onlyInGithub} showStatus />}
                    </Box>
                  </Box>
                )}

                {/* Method Changes — grouped by class */}
                {changesTab === 1 && (
                  <Box sx={{ p: 2 }}>
                    {methodChanges.length === 0 ? (
                      <Typography color="text.secondary">No method changes returned from Drill4J.</Typography>
                    ) : (() => {
                      // Group methods by className
                      const grouped = methodChanges.reduce((acc, m) => {
                        const key = m.className || m.simpleClassName || '(unknown)';
                        if (!acc[key]) {
                          acc[key] = { simpleClassName: m.simpleClassName || key, githubLink: m.githubLink, methods: [] };
                        }
                        if (!acc[key].methods.includes(m.methodName)) {
                          acc[key].methods.push(m.methodName);
                        }
                        return acc;
                      }, {});

                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {Object.entries(grouped).map(([key, group]) => (
                            <Box key={key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                              {/* Class name header */}
                              <Typography
                                variant="subtitle2"
                                sx={{ fontFamily: 'monospace', fontWeight: 700, mb: 1, color: 'text.primary' }}
                              >
                                {group.githubLink ? (
                                  <Link
                                    href={group.githubLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                                  >
                                    {group.simpleClassName}
                                  </Link>
                                ) : group.simpleClassName}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  ({group.methods.length} method{group.methods.length !== 1 ? 's' : ''})
                                </Typography>
                              </Typography>
                              {/* Methods as chips */}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pl: 1 }}>
                                {group.methods.map((method) => (
                                  <Chip
                                    key={method}
                                    label={method}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      );
                    })()}
                  </Box>
                )}

                {/* Merged PRs */}
                {changesTab === 2 && (
                  <Box sx={{ p: 2 }}>
                    {prs.length === 0 ? (
                      <Typography color="text.secondary">No merged PRs recorded for this run.</Typography>
                    ) : (
                      <List dense disablePadding>
                        {prs.map((pr) => (
                          <ListItem key={pr.prNumber} divider alignItems="flex-start" sx={{ py: 1 }}>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                  <Link
                                    href={pr.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    fontWeight={700}
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    #{pr.prNumber}
                                  </Link>
                                  <Typography variant="body2">{pr.title}</Typography>
                                </Box>
                              }
                              secondary={
                                <Box display="flex" gap={1} mt={0.5} flexWrap="wrap" alignItems="center">
                                  {pr.author && <Chip label={pr.author} size="small" variant="outlined" />}
                                  {pr.targetBranch && <Chip label={`→ ${pr.targetBranch}`} size="small" variant="outlined" />}
                                  {pr.mergedAt && (
                                    <Typography variant="caption" color="text.secondary">
                                      {new Date(pr.mergedAt).toLocaleDateString()}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangesDialog({ ...changesDialog, open: false })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/** Reusable file table used in all three file-change categories */
const FileTable = ({ files, showStatus }) => {
  if (!files || files.length === 0) {
    return <Typography color="text.secondary">No files in this category.</Typography>;
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600 }}>File</TableCell>
          {showStatus && <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>}
          {showStatus && <TableCell sx={{ fontWeight: 600 }} align="right">+</TableCell>}
          {showStatus && <TableCell sx={{ fontWeight: 600 }} align="right">−</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {files.map((f, i) => (
          <TableRow key={i} hover>
            <TableCell>
              <Link
                href={f.link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}
              >
                {f.filename}
              </Link>
            </TableCell>
            {showStatus && (
              <TableCell>
                <Chip
                  label={f.status || 'modified'}
                  size="small"
                  color={f.status === 'added' ? 'success' : f.status === 'removed' ? 'error' : 'default'}
                  variant="outlined"
                />
              </TableCell>
            )}
            {showStatus && (
              <TableCell align="right">
                <Typography variant="body2" color="success.main">
                  {f.additions != null ? `+${f.additions}` : ''}
                </Typography>
              </TableCell>
            )}
            {showStatus && (
              <TableCell align="right">
                <Typography variant="body2" color="error.main">
                  {f.deletions != null ? `−${f.deletions}` : ''}
                </Typography>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default DailyRegressionTab;
